// Typed static client for the /Drug/* endpoints.
// All-static, returns typed data (never a raw AxiosResponse), rethrows errors.
// Follows the PositiveIdService precedent.
//
// URL note: axios.defaults.baseURL already ends in /api. Endpoint strings are
// preserved verbatim from PrescriptionDetailsPage.vue's original call sites,
// including the deliberate mix of leading-slash forms (`/Drug/...` vs
// `Drug/...`) and the /api-prefixed patient drug-history route. Mechanical,
// behavior-preserving — the exact request paths must not change.

import axios from 'axios'
import { Drug } from '@/models/Drug/Drug'
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct'
import { DrugCategory } from '@/models/DrugCategory'
import { Inventory } from '@/models/Inventory'
import { AllergyScreeningResult } from '@/models/AllergyScreening'

/** Standard drug-lookup params shared by details and alert. */
const detailParams = (drugSource: DrugSource) => ({ getProgram: true, getVendors: true, drugSource })

export class DrugService {
    /** Full drug product (program + vendors) for the given source. */
    static async getDetails(drugId: number, drugSource: DrugSource): Promise<DrugProduct> {
        const response = await axios.get<DrugProduct>(`/Drug/${drugId}`, { params: detailParams(drugSource) })
        return response.data
    }

    /** Per-store inventory rows for the drug, filling locations only. */
    static async getInventory(drugId: number, drugSource: DrugSource): Promise<Inventory[]> {
        const response = await axios.get<Inventory[]>(`/Drug/${drugId}/Inventory`, {
            params: { drugSource, fillingLocationsOnly: true },
        })
        return response.data
    }

    /** Drug alert text for the given source. */
    static async getAlert(drugId: number, drugSource: DrugSource): Promise<string> {
        const response = await axios.get<string>(`/Drug/${drugId}/Alert`, { params: detailParams(drugSource) })
        return response.data
    }

    /** DEA-schedule expiration window (days) for a package in a given state. */
    static async getScheduledExpiration(packageId: number, source: DrugSource, addressState: string): Promise<number> {
        const response = await axios.get<number>(`Drug/${packageId}/${source}/${addressState}/ScheduledExpiration`)
        return response.data
    }

    /** Drug-category custom questions for a product. */
    static async getCategories(productId: number | string, source: DrugSource): Promise<DrugCategory[]> {
        const response = await axios.get<DrugCategory[]>(`Drug/${productId}/${source}/categories`)
        return response.data
    }

    /**
     * Generic-substitution equivalents for a product/package — backs the save
     * generic-substitution preflight (SavePorts.api.getEquivalents). Distinct
     * from PrescriptionService.getEquivalentPrescriptions, which backs the
     * equivalent-prescriptions gate.
     */
    static async getEquivalents(
        productId: number,
        packageId: number,
        storeId: number | undefined,
    ): Promise<Drug.EquivalentDrug[]> {
        const response = await axios.get<Drug.EquivalentDrug[]>(`/Drug/${productId}/Equivalent/${packageId}`, {
            params: { onlyActive: true, storeId },
        })
        return response.data
    }

    /** Last-filled date for a patient's drug (drug-history route lives under /api/Patient). */
    static async getPatientDrugHistory(
        patientId: number,
        drugSource: DrugSource,
        drugId: number,
        rxId: string,
    ): Promise<string> {
        const response = await axios.get<string>(`/api/Patient/${patientId}/drug-history/${drugSource}/${drugId}/${rxId}`)
        return response.data
    }

    /** Screens a drug against the patient's recorded allergies at the point the drug is selected. */
    static async screenAllergies(
        patientId: number,
        drugId: number,
        drugSource: DrugSource,
    ): Promise<AllergyScreeningResult> {
        const response = await axios.post<AllergyScreeningResult>('/Drug/allergy-check', {
            patientId,
            drugId,
            drugSource,
        })
        return response.data
    }
}

export default DrugService
